import { Component, ElementRef, EventEmitter, Input, OnDestroy, Output, TemplateRef, ViewChild, ViewContainerRef, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { RouterLink } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { Overlay, OverlayModule, OverlayRef } from '@angular/cdk/overlay';
import { MatDialog } from '@angular/material/dialog';
import { TemplatePortal } from '@angular/cdk/portal';
import { Subscription } from 'rxjs';
import { finalize, take } from 'rxjs/operators';
import days from 'dayjs';

import { DeliveryPlanning } from '../../logistic.service';
import { BlPreview, DelrunsService } from '../delruns.service';
import { parseTz } from '@app/shared/time';
import { BL_CLOSE_GRACE_MS, BL_HOVER_DELAY_MS } from '@app/shared/constants';
import { Attach, C_value } from '@app/shared/_models/common';
import { LogisticParamsService } from '@app/shared/logistic-params.service';
import { AttachService } from '@app/shared/attach/attach.service';
import { AlertService } from '@app/shared/alert.service';
import { PrintService } from '@app/shared/print.service';
import { SpinnerService } from '@app/shared/spinner.service';
import { RentalDirectionComponent, rentalDirection } from '@app/shared/rental-direction/rental-direction.component';
import { RentalDeliveryDialogComponent } from '@app/rental/rental-delivery-dialog/rental-delivery-dialog.component';

// Carte d'une livraison, réutilisée dans la colonne « À planifier » et dans les tournées.
// Rappelle ce qui a été convenu avec le client : date souhaitée + créneau/opérateur (c_values 8 & 9).
// Une livraison réalisée (realdatdel non nul = BL signé) est badgée et n'est plus déplaçable.
//
// Aperçu du CONTENU du BL : survol 1 s OU clic sur l'icône œil (panneau épinglé — tablette tactile)
// => panneau flottant (cdk Overlay ancré à la carte) avec les lignes du BL : qtés partielles « x/y »,
// lignes orphelines (« Ligne #id »), n° de BL x/y de la commande, lien « Ouvrir la planification BL », imprimante.
// Données via DelrunsService.getOrderWithBls$ (GET /sales/order/:id?o=deliveries, cache par commande).
// Jamais de panneau pendant un drag : mousedown annule tout, survol ignoré si un bouton est enfoncé.
@Component({
	selector: 'app-delivery-card',
	imports: [RouterLink, MatIconModule, MatButtonModule, OverlayModule, RentalDirectionComponent],
	templateUrl: './delivery-card.component.html',
	styleUrl: './delivery-card.component.scss',
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: true,
})
export class DeliveryCardComponent implements OnDestroy {

	@Input({ required: true }) delivery!: DeliveryPlanning;
	@Input() slots: C_value[] = [];		// c_values 8 (créneaux)
	@Input() ops: C_value[] = [];			// c_values 9 (opérateurs)
	@Input() seq: number | null = null;		// n° d'ordre dans la tournée (null hors tournée)
	@Input() ref: string | null = null;		// repère de carte (A, B, … AA) : badge affiché hors tournée, dans
																				// le coin bas droit — c'est la lettre lue sur l'épingle grise
	@Input() color: string | null = null;	// couleur de la tournée : le n° d'arrêt la porte, comme son épingle
	@Input() editDuration = false;				// affiche l'input « temps sur place » (contexte tournée)
	@Input() highlight = false;					// BL tout juste planifié depuis la fenêtre de livraison (?bl= du board)

	@Output() durationChange = new EventEmitter<void>();		// temps sur place modifié (le board recalcule + re-valide)

	@ViewChild('blPanel') private blPanelTpl!: TemplateRef<unknown>;

	private delrunsService = inject(DelrunsService);
	private logisticParams = inject(LogisticParamsService);
	private attachService = inject(AttachService);
	private alertService = inject(AlertService);
	private printService = inject(PrintService);
	private spin = inject(SpinnerService);
	private dialog = inject(MatDialog);
	private overlay = inject(Overlay);
	private viewContainerRef = inject(ViewContainerRef);
	private elementRef: ElementRef<HTMLElement> = inject(ElementRef);

	// --- état de l'aperçu BL ---
	blPreview = signal<BlPreview | null>(null);		// contenu chargé (conservé : pas de re-fetch au re-survol)
	blLoading = signal(false);
	blError = signal(false);
	blPinned = signal(false);
	// Une location dans le BL : toutes ses lignes réservent la case de la flèche, les quantités restent alignées
	blHasDirection = computed(() => this.blPreview()?.lines.some((l) => rentalDirection(l.typ, l.qty) !== null) ?? false);

	// --- état du mémo (attach is_default_memo de la livraison) ---
	// Le texte vit ici et non dans le DOM : le panneau est un Overlay, il peut être détruit en pleine
	// saisie (Échap, clic ailleurs) — la frappe serait perdue avant d'avoir été lue.
	memo = signal('');
	memoSaving = signal(false);
	printing = signal(false);
	private printAfterMemo = false;		// imprimante cliquée pendant l'envoi du mémo : le BL non signé l'imprime, on attend sa réponse
	private memoAttach: Attach | null = null;		// null tant que le BL n'est pas chargé
	private memoSaved = '';											// dernier texte PERSISTÉ : la comparaison dit s'il y a quelque chose à sauver

	private overlayRef: OverlayRef | null = null;
	private hoverTimer: ReturnType<typeof setTimeout> | null = null;
	private closeTimer: ReturnType<typeof setTimeout> | null = null;
	private blSub: Subscription | null = null;
	private panelSubs: Subscription | null = null;

	get customerName(): string { return DelrunsService.customerNameOf(this.delivery); }

	get address(): string { return DelrunsService.addressLabel(DelrunsService.deliveryAddressOf(this.delivery)); }

	// Adresse complète (toutes lignes + les cufs d'adresse valués) pour le panneau BL — jamais tronquée
	get addressDetails(): string[] {
		return this.delrunsService.addressDetails(DelrunsService.deliveryAddressOf(this.delivery));
	}

	// Temps sur place en minutes (défaut paramétré appliqué si non renseigné)
	get durationMin(): number {
		return Math.round((this.delivery?.duration ?? this.logisticParams.params().default_duration_s) / 60);
	}

	// Pas des flèches haut/bas (minutes) : paramètre de configuration, 5 min en son absence.
	// Même règle que le champ de la page « Programmation de livraison » (PlanificationdeliveryComponent).
	get durationStepMin(): number {
		const step = this.logisticParams.params().duration_step_min;
		return step > 0 ? step : 5;
	}

	onDurationMinChange(v: string): void {		// saisie en minutes -> delivery.duration (secondes)
		const min = (v === '' || isNaN(+v) || +v < 0) ? null : Math.min(Math.round(+v), 999);		// 3 digits max
		this.delivery.duration = min === null ? null : min * 60;
		this.durationChange.emit();
	}

	// Chiffres uniquement, 3 max : un <input type="number"> laisse passer lettres / e / +- (Firefox les affiche)
	onDurationKeydown(ev: KeyboardEvent): void {
		if (ev.key.length !== 1 || ev.ctrlKey || ev.metaKey) return;
		if (!/\d/.test(ev.key)) { ev.preventDefault(); return; }
		const el = ev.target as HTMLInputElement;
		const sel = (el.selectionEnd ?? 0) - (el.selectionStart ?? 0);
		if (el.value.length - sel >= 3) ev.preventDefault();
	}

	get tel(): string {
		const c = this.delivery?.lnk_order?.lnk_customer;
		return c?.tel1 || c?.tel2 || '';
	}

	// Rappel du souhait client : « Début de matinée », « Avant 9h », « Livraison dès que possible »…
	get askLabel(): string { return DelrunsService.opAndSlotLabel(this.delivery, this.ops, this.slots); }

	get askDate(): string {
		return this.delivery?.mode === 4 && this.delivery?.askdatdel ? days(this.delivery.askdatdel).format('DD/MM') : '';
	}

	get signed(): boolean { return !!this.delivery?.realdatdel; }

	get rental(): boolean { return this.delivery?.lnk_order?.doc_type === 'LOCATION'; }

	// Location : ce BL seul dans la fenêtre de livraison location, sans l'historique du dossier (l'ancienne fenêtre lit tous ses BL).
	openRentalBl(ev: Event): void {
		ev.preventDefault();
		this.closePreview();
		const ref = this.dialog.open(RentalDeliveryDialogComponent, { width: '90%', height: '90%', maxWidth: '95vw', data: { id: this.delivery.id_order, deliveryId: this.delivery.id } });
		ref.beforeClosed().subscribe(() => {
			if (!ref.componentInstance?.wrote) return;
			this.blPreview.set(null);		// aperçu relu au prochain survol
			this.delrunsService.blEdited$.next();
		});
	}

	get asap(): boolean { return this.delivery?.mode === 3; }

	// Repère de carte affiché : hors tournée seulement — dans une tournée, le n° d'arrêt dit déjà tout
	get showRef(): boolean { return this.seq === null && !!this.ref; }

	get products(): string[] {
		return (this.delivery?.lnk_order?.lines ?? []).map((l) => l.qty + ' ' + l.product_name);
	}

	get signedDateLabel(): string {
		// Heure murale MÉTIER (parseTz), pas celle du navigateur : le BL a été signé à l'heure du magasin
		const dt = this.blPreview()?.realdatdel;
		return dt ? parseTz(dt)?.format('DD/MM HH:mm') ?? '' : '';
	}

	// Le back renvoie des qtés float : 12.0 => « 12 », 2.5 => « 2,5 »
	qtyOf(n: number | null): string {
		return n === null || n === undefined ? '' : String(+n).replace('.', ',');
	}

	// Récap du BL : « 3 lignes · 18 articles »
	totalOf(p: BlPreview): string {
		const total = p.lines.reduce((s, l) => s + (+l.qty || 0), 0);
		return p.lines.length + ' ligne' + (p.lines.length > 1 ? 's' : '') + ' · ' + this.qtyOf(total) + ' article' + (total > 1 ? 's' : '');
	}

	// ============================================================
	// Aperçu BL — survol différé / épinglage
	// ============================================================

	// Survol : ouverture différée — ignoré si un bouton souris est enfoncé (drag en cours sur le board)
	onCardEnter(ev: MouseEvent): void {
		this.cancelScheduledClose();
		if (this.overlayRef || ev.buttons !== 0) return;
		this.hoverTimer = setTimeout(() => this.openPreview(false), BL_HOVER_DELAY_MS);
	}

	// Sortie de la carte (ou du panneau) : fermeture différée, sauf panneau épinglé.
	// Le délai de grâce laisse le temps de passer de la carte au panneau sans le fermer.
	onCardLeave(): void {
		this.cancelHoverTimer();
		if (this.overlayRef && !this.blPinned()) {
			this.closeTimer = setTimeout(() => this.closePreview(), BL_CLOSE_GRACE_MS);
		}
	}

	// mousedown = début potentiel de drag : jamais de panneau pendant un drag
	onCardDown(): void {
		this.cancelHoverTimer();
		if (!this.blPinned()) this.closePreview();
	}

	// Icône œil : ouverture immédiate épinglée (tablette tactile) ; ré-clic = fermeture.
	// stopPropagation : le clic ne doit ni démarrer un drag ni compter comme « clic hors panneau ».
	onTogglePin(ev: Event): void {
		ev.stopPropagation();
		if (this.overlayRef && this.blPinned()) {
			this.closePreview();
			return;
		}
		this.openPreview(true);
	}

	cancelScheduledClose(): void {
		if (this.closeTimer) { clearTimeout(this.closeTimer); this.closeTimer = null; }
	}

	private cancelHoverTimer(): void {
		if (this.hoverTimer) { clearTimeout(this.hoverTimer); this.hoverTimer = null; }
	}

	private openPreview(pinned: boolean): void {
		this.cancelHoverTimer();
		this.cancelScheduledClose();
		if (pinned) this.blPinned.set(true);
		if (this.overlayRef) return;		// déjà ouvert au survol : l'épinglage réutilise le panneau
		this.overlayRef = this.overlay.create({
			positionStrategy: this.overlay.position()
				.flexibleConnectedTo(this.elementRef)
				.withPositions([
					{ originX: 'end', originY: 'top', overlayX: 'start', overlayY: 'top', offsetX: 8 },
					{ originX: 'start', originY: 'top', overlayX: 'end', overlayY: 'top', offsetX: -8 },
					{ originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top', offsetY: 8 },
					{ originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom', offsetY: -8 },
				])
				.withPush(true)
				.withViewportMargin(8),
			scrollStrategy: this.overlay.scrollStrategies.reposition(),
		});
		this.overlayRef.attach(new TemplatePortal(this.blPanelTpl, this.viewContainerRef));
		this.panelSubs = new Subscription();
		this.panelSubs.add(this.overlayRef.keydownEvents().subscribe((e) => {
			if (e.key === 'Escape') this.closePreview();
		}));
		this.panelSubs.add(this.overlayRef.outsidePointerEvents().subscribe((e) => {
			// épinglé : un clic hors carte + hors panneau ferme (un clic sur la carte est laissé au lien commande…)
			if (!this.elementRef.nativeElement.contains(e.target as Node)) this.closePreview();
		}));
		this.loadBl();
	}

	closePreview(): void {
		this.saveMemoIfDirty();		// Échap / clic ailleurs / carte détruite : le champ disparaît sans blur fiable
		this.cancelScheduledClose();
		this.blPinned.set(false);
		this.blSub?.unsubscribe();
		this.blSub = null;
		this.panelSubs?.unsubscribe();
		this.panelSubs = null;
		this.overlayRef?.dispose();
		this.overlayRef = null;
		this.blLoading.set(false);
	}

	// Charge le contenu du BL (une fois par carte : blPreview est conservé, le service cache par commande)
	private loadBl(): void {
		if (this.blPreview() || this.blLoading()) return;
		this.blLoading.set(true);
		this.blError.set(false);
		this.blSub = this.delrunsService.getOrderWithBls$(this.delivery.id_order).pipe(take(1)).subscribe({
			next: (order) => {
				this.blLoading.set(false);
				this.blPreview.set(DelrunsService.blPreviewOf(order, this.delivery.id));
				if (!this.blPreview()) { this.blError.set(true); return; }		// BL introuvable dans la commande (défensif)
				this.memoAttach = this.delrunsService.memoAttachOf(order, this.delivery.id);
				this.memo.set(this.memoAttach.memo ?? '');
				this.memoSaved = this.memo();
			},
			error: () => {
				this.blLoading.set(false);
				this.blError.set(true);
			},
		});
	}

	// ============================================================
	// Mémo du BL — édition à la volée, enregistrement au blur
	// ============================================================

	// La saisie ÉPINGLE le panneau : ouvert au survol, il se fermerait dès que la souris s'en écarte,
	// en pleine frappe. Épinglé, il ne part plus que sur Échap / clic ailleurs / icône œil.
	onMemoFocus(): void {
		this.cancelScheduledClose();
		this.blPinned.set(true);
	}

	onMemoInput(v: string): void {
		this.memo.set(v);
	}

	onMemoBlur(): void {
		this.saveMemoIfDirty();
	}

	// Enregistrement direct sur l'attach (event ATTACH_SAVE_MEMO, id 0 => création) : il cible l'attach et
	// non la livraison, donc il passe même sur un BL signé. Rien n'est envoyé si le texte n'a pas changé —
	// le blur et la fermeture appellent tous deux, l'un après l'autre.
	private saveMemoIfDirty(): void {
		const attach = this.memoAttach;
		if (!attach || this.memo() === this.memoSaved) return;		// inchangé : rien à sauver
		// Envoi déjà en vol : on attend sa réponse (voir le rappel dans next). Un 2e POST partirait encore
		// en id 0 — l'id de création n'est pas connu — et créerait un SECOND attach mémo sur la livraison.
		if (this.memoSaving()) return;
		const sent = this.memo();
		attach.memo = sent;
		this.memoSaving.set(true);
		// Souscription volontairement hors de blSub / panelSubs : celles-ci sont coupées à la fermeture du
		// panneau et à la destruction de la carte, ce qui ANNULERAIT le POST qu'on vient d'émettre.
		this.attachService.saveMemo$(attach).subscribe({
			next: (response: any) => {
				this.memoSaving.set(false);
				if (!attach.id && response?.data?.id) attach.id = response.data.id;		// création : l'id évite le doublon au prochain envoi
				this.memoSaved = sent;
				this.saveMemoIfDirty();		// le texte a encore bougé pendant l'envoi : on repart, l'id est connu cette fois
				this.printIfMemoSettled();
			},
			error: (e) => {
				// memoSaved inchangé => le texte reste « à sauver » : le prochain blur / la fermeture réessaient
				this.memoSaving.set(false);
				this.alertService.error("ERREUR : le mémo n'a pas été enregistré");
				console.error(e);
				this.printIfMemoSettled();
			},
		});
	}

	// ============================================================
	// Imprimante de l'aperçu — ce BL seul, sans rien écrire
	// ============================================================

	// GET pur (signé tel qu'archivé, sinon rendu du jour) : ni l'event PRINT de la fenêtre de livraison, qui réécrit les lignes, ni fenêtre ouverte.
	printBl(): void {
		if (this.printing()) return;
		this.printing.set(true);
		this.printAfterMemo = true;
		this.printIfMemoSettled();		// le clic a fait perdre le focus au mémo : son envoi part juste avant
	}

	private printIfMemoSettled(): void {
		if (!this.printAfterMemo || this.memoSaving()) return;
		this.printAfterMemo = false;
		// Hors de blSub / panelSubs : le panneau peut se fermer sous le voile sans annuler l'impression.
		this.delrunsService.getDeliveryBl$(this.delivery.id).pipe(this.spin.block(`BL #${this.delivery.id}…`), finalize(() => this.printing.set(false))).subscribe({
			next: (blob) => this.printService.printPdf(blob),
			error: (e) => {
				console.error(e);
				this.alertService.error('ERREUR lors de la génération du BL');
			},
		});
	}

	ngOnDestroy(): void {
		this.cancelHoverTimer();
		this.closePreview();		// enregistre le mémo en cours (saveMemoIfDirty)
	}
}
